import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DatePicker, { registerLocale } from 'react-datepicker';
import InputMask from 'react-input-mask';
import { isEmpty } from 'lodash';
import { v4 as uuid } from 'uuid';
import es from 'date-fns/locale/es';
import moment from 'moment';
import clsx from 'clsx';

import {
  months,
  iconCalendar,
  iconArrowLeft,
  iconArrowRight,
} from './defaults';
import dateExistsInCalendar from '../../../utils/dateExistsInCalendar';

registerLocale('es', es);

const InputPicker = ({
  id,
  onChange,
  onBlur,
  disabled,
  className,
  label,
  value,
  maxDate,
  minDate,
  mask,
  formatPicker,
  icon,
  hasError,
  untilToday,
}) => {
  const [maskValue, setMaskValue] = useState(null);
  const [editable, setEditable] = useState(false);

  const onChangeMask = (event) => {
    const valueMask = event.target.value;

    setEditable(true);
    setMaskValue(valueMask);

    if ((valueMask || '').trim().length === 0) {
      onChange({ value: null, hasErrorFormat: false });
      return;
    }

    if (!valueMask.includes('_')) {
      const splitDate = valueMask.split('/');
      const year = splitDate[2];
      const month = splitDate[1];
      const day = splitDate[0];
      const date = new Date(`${month}/${day}/${year}`);

      if (
        moment(date).isValid() &&
        dateExistsInCalendar(Number(year), Number(month) - 1, Number(day))
      ) {
        const today = new Date();
        if (untilToday && moment(date) > today) {
          onChange({ value: date, hasErrorFormat: true });
          return;
        }
        if (untilToday && moment(date) < minDate) {
          console.log('entro');
          onChange({ value: date, hasErrorFormat: true });
          return;
        }
        onChange({ value: date, hasErrorFormat: false });
        return;
      }
    }
    onChange({ value: null, hasErrorFormat: true });
  };

  useEffect(() => {
    if (
      !editable &&
      moment(value).isValid() &&
      moment(value).format(formatPicker) !== maskValue
    ) {
      setMaskValue(moment(value).format(formatPicker));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <>
      <div
        id={id}
        className={clsx(
          'rd--input',
          'rd--input--picker',
          'active',
          'filled',
          className,
          { 'has-error': hasError, disabled }
        )}
        disabled={disabled}
      >
        {!isEmpty(label) && <label>{label}</label>}
        <InputMask
          data-testid="date-input"
          placeholder="DD/MM/AAAA"
          mask={mask}
          onChange={onChangeMask}
          value={maskValue ?? ''}
          onClick={() => setEditable(true)}
          onBlur={() => {
            setEditable(false);
            onBlur();
          }}
        />
        <DatePicker
          locale="es"
          selected={value}
          onChange={(value) => onChange({ value, hasErrorFormat: false })}
          maxDate={maxDate}
          minDate={minDate}
          customInput={<button>{icon}</button>}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="rd--custom__head_year">
              <button
                className="rd--arrow rd--left--date"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
              >
                {iconArrowLeft}
              </button>
              <label>{`${
                months[date.getMonth()]
              } ${date.getFullYear()}`}</label>
              <button
                className="rd--arrow rd--right--date"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
              >
                {iconArrowRight}
              </button>
            </div>
          )}
        />
      </div>
      {hasError && (
        <span className="rd--input-error" htmlFor={id}>
          {hasError}
        </span>
      )}
    </>
  );
};

InputPicker.propTypes = {
  id: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  mask: PropTypes.string.isRequired,
  formatPicker: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  hasError: PropTypes.any,
  untilToday: PropTypes.bool,
};

InputPicker.defaultProps = {
  id: uuid(),
  onChange: () => {},
  onBlur: () => {},
  disabled: false,
  className: '',
  label: '',
  value: null,
  maxDate: null,
  minDate: null,
  mask: '99/99/9999',
  formatPicker: 'DD-MM-YYYY',
  icon: iconCalendar,
  hasError: null,
  untilToday: false,
};

export default InputPicker;
